import { useApolloClient } from '@apollo/client';
import { Col, Layout, Result, Row, Spin } from 'antd';
import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/nextjs';
import { useQueryClient as useTanstackClient } from '@tanstack/react-query';
import { mySession } from '../../helpers/gqlQueries/me';
import { SessionProvider } from '../../lib/SessionContext';
import { AnalyticsProvider } from '../../lib/analytics';
import { useUnauthedGqlQuery } from '../../lib/hooks';
import { getEnv } from '../../environment/resolve';
let lastKeepAlive = new Date();
const keepSessionAlive = () => {
  if (new Date() - lastKeepAlive > 60000) {
    fetch(getEnv().keepSessionAliveURL, {
      credentials: 'include',
      mode: 'no-cors'
    });
    lastKeepAlive = new Date();
  }
};
const eventHandler = ['click', keepSessionAlive, {
  passive: true
}];
const SessionController = ({
  children
}) => {
  const apolloClient = useApolloClient();
  const tanstackClient = useTanstackClient();
  const [prevUserId, setPrevUserId] = useState(null);
  const {
    isPending,
    error,
    data
  } = useUnauthedGqlQuery(mySession, {}, {
    queryKey: ['session'],
    staleTime: 0,
    notifyOnChangeProps: ['status', 'error', 'data']
  });
  const currUserId = data?.me?.user?.id ?? null;
  const state = {
    refresh: () => tanstackClient.resetQueries({
      queryKey: ['session']
    }),
    menu: [],
    ...(!isPending && !error && data?.me || {})
  };
  const userChanged = !isPending && prevUserId !== currUserId;
  if (userChanged) {
    Promise.all([apolloClient.clearStore(), tanstackClient.invalidateQueries({
      queryKey: ['authed'],
      refetchType: 'none'
    })]).then(() => setPrevUserId(currUserId));
  }
  useEffect(() => {
    if (state.user) {
      document.addEventListener(...eventHandler);
      Sentry.setUser({
        id: state.user.id,
        username: state.user.name
      });
      Sentry.setTag('user.type', state.user.type);
      return () => {
        document.removeEventListener(...eventHandler);
        Sentry.setUser(null);
        Sentry.setTag('user.type', null);
      };
    }
  }, [state]);
  if (userChanged || isPending || error) {
    return <Layout style={{
      height: '100vh'
    }}>
				<Layout.Content style={{
        alignItems: 'center'
      }}>
					<Row type="flex" justify="center" align="middle" style={{
          height: '100%'
        }}>
						<Col span={8} style={{
            textAlign: 'center'
          }}>
							<Result icon={error ? undefined : <Spin size="large" />} status={error ? "error" : "info"} title={error ? "An error occurred during logging in. Reload the page to try again." : "Loading..."} />
						</Col>
					</Row>
				</Layout.Content>
			</Layout>;
  }
  return <SessionProvider value={Object.seal(state)} data-sentry-element="SessionProvider" data-sentry-component="SessionController" data-sentry-source-file="SessionController.jsx">
			<AnalyticsProvider data-sentry-element="AnalyticsProvider" data-sentry-source-file="SessionController.jsx">
				<main children={children} />
			</AnalyticsProvider>
		</SessionProvider>;
};
export default SessionController;